import type React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import type {
  IMaintenanceHistory,
  IEquipmentDetailDialogProps,
} from "../../types/equipment";
import { tokens } from "../../theme";

const EquipmentDetailDialog: React.FC<IEquipmentDetailDialogProps> = ({
  open,
  onClose,
  equipment,
  onScheduleMaintenance,
}) => {
  if (!equipment) return null;

  const maintenanceHistory: IMaintenanceHistory[] = [
    {
      id: "1",
      type: "Scheduled Maintenance",
      description: "Lense cleaning",
      date: "March 15, 2025",
    },
    {
      id: "2",
      type: "Scheduled Maintenance",
      description: "Full service",
      date: "March 15, 2025",
    },
    {
      id: "3",
      type: "Scheduled Maintenance",
      description: "Lense cleaning",
      date: "March 15, 2025",
    },
  ];
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.3 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: colors.primary[300], fontWeight: "bold" }}
        >
          {equipment.name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid
          container
          spacing={2}
          sx={{ backgroundColor: colors.primary[800], p: 2, borderRadius: 3 }}
        >
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Name
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.name}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body1" sx={{ color: colors.primary[300] }}>
              Serial No.
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.serialNo}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Brand
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.brand}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Sub Category
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.subCategory}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Category
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.category}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 2, p: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Last service date
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.lastServiceDate || "N/A"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Hours since last service
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.hoursSinceLastService
                ? `${equipment.hoursSinceLastService}hrs`
                : "N/A"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Assignee
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: colors.primary[100], fontWeight: "bold" }}
            >
              {equipment.assignee || "N/A"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              Maintenance
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: colors.primary[100], fontWeight: "bold" }}
            >
              Up to date
            </Typography>
          </Grid>
        </Grid>

        <Box
          sx={{
            backgroundColor: colors.primary[800],
            p: 2,
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: colors.primary[100], fontWeight: "bold" }}
          >
            History Maintenance
          </Typography>
          <List>
            {maintenanceHistory.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <ListItem
                  sx={{
                    backgroundColor: colors.primary[900],
                  }}
                >
                  <ListItemText
                    primary={item.type}
                    secondary={item.description}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: colors.primary[100] }}
                  >
                    {item.date}
                  </Typography>
                </ListItem>
                {index < maintenanceHistory.length - 1 && <Divider />}
              </motion.div>
            ))}
          </List>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: colors.primary[800],
            borderRadius: 2,
            color: colors.primary[100],
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onScheduleMaintenance}
          variant="contained"
        >
          Schedule for Maintenance
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentDetailDialog;
